import { Injectable, Inject } from "@angular/core";
import { Meta, Title } from "@angular/platform-browser";
import { DOCUMENT } from "@angular/common";

@Injectable({
  providedIn: "root",
})
export class SeoService {
  constructor(
    private meta: Meta,
    private title: Title,
    @Inject(DOCUMENT) private document: Document
  ) {}

  setTitle(title: string): void {
    // Set title directly without automatic suffix to keep under 60 characters
    this.title.setTitle(title.trim());
  }

  setDescription(description: string) {
    this.meta.updateTag({ name: "description", content: description });
  }

  setKeywords(keywords: string[]) {
    this.meta.updateTag({ name: "keywords", content: keywords.join(", ") });
  }

  /**
   * Ensure a single correct <link rel="canonical"> element exists.
   * Angular's Meta service cannot update <link> tags, so we manipulate the
   * DOM directly via the injected DOCUMENT. Works both in the browser and
   * during SSR.
   */
  setCanonicalUrl(url: string): void {
    try {
      const u = new URL(url);
      if (u.hostname.startsWith("www.")) {
        u.hostname = u.hostname.replace(/^www\./, "");
        url = u.toString();
      }
    } catch (_) {
      // If URL constructor fails (unlikely), fall back to original string
    }

    // Update og:url for completeness
    this.meta.updateTag({ property: "og:url", content: url });

    const head = this.document.head;
    if (!head) return;

    let linkEl = head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

    if (!linkEl) {
      linkEl = this.document.createElement("link");
      linkEl.setAttribute("rel", "canonical");
      head.appendChild(linkEl);
    }

    linkEl.setAttribute("href", url);
  }

  setOpenGraphTags(config: {
    title: string;
    description: string;
    url: string;
    image?: string;
    type?: string;
    locale?: string;
  }) {
    this.meta.updateTag({ property: "og:title", content: config.title });
    this.meta.updateTag({
      property: "og:description",
      content: config.description,
    });
    this.meta.updateTag({ property: "og:url", content: config.url });
    this.meta.updateTag({ property: "og:site_name", content: "Design Jobs Careers" });

    if (config.image) {
      this.meta.updateTag({ property: "og:image", content: config.image });
      this.meta.updateTag({ property: "og:image:alt", content: config.title });
      this.meta.updateTag({ property: "og:image:width", content: "1200" });
      this.meta.updateTag({ property: "og:image:height", content: "630" });
      this.meta.updateTag({ property: "og:image:type", content: "image/png" });
    }

    this.meta.updateTag({
      property: "og:type",
      content: config.type || "website",
    });

    // Set locale if provided
    if (config.locale) {
      this.meta.updateTag({ property: "og:locale", content: config.locale });
    }
  }

  setTwitterTags(config: {
    title: string;
    description: string;
    image?: string;
  }) {
    this.meta.updateTag({
      name: "twitter:card",
      content: "summary_large_image",
    });
    this.meta.updateTag({ name: "twitter:site", content: "@Designjobs6" });
    this.meta.updateTag({ name: "twitter:creator", content: "@Designjobs6" });
    this.meta.updateTag({ name: "twitter:title", content: config.title });
    this.meta.updateTag({
      name: "twitter:description",
      content: config.description,
    });

    if (config.image) {
      this.meta.updateTag({ name: "twitter:image", content: config.image });
      this.meta.updateTag({ name: "twitter:image:src", content: config.image });
    }
  }

  setJobPostingTags(config: {
    title: string;
    description: string;
    url: string;
    image?: string;
    company: string;
    location?: string;
    salary?: string;
    workType?: string;
    employmentType?: string;
  }) {
    // Set standard Open Graph tags
    this.setOpenGraphTags({
      title: config.title,
      description: config.description,
      url: config.url,
      image: config.image,
      type: "article",
      locale: "en_US",
    });

    // Set Twitter Card tags with URL
    this.setTwitterTags({
      title: config.title,
      description: config.description,
      image: config.image,
    });

    // Set Twitter URL explicitly
    this.meta.updateTag({ name: "twitter:url", content: config.url });

    // Add LinkedIn-specific Open Graph tags for better job previews
    this.meta.updateTag({ property: "og:site_name", content: "Design Jobs Careers" });
    this.meta.updateTag({ property: "og:type", content: "article" });

    // Enhanced LinkedIn meta tags
    this.meta.updateTag({
      name: "linkedin:card",
      content: "summary_large_image",
    });

    // Add more descriptive meta for LinkedIn crawler
    this.meta.updateTag({
      name: "description",
      content: config.description,
    });

    // Add job-specific meta tags
    this.meta.updateTag({ name: "job:company", content: config.company });
    this.meta.updateTag({ property: "job:company", content: config.company });

    if (config.location) {
      this.meta.updateTag({ name: "job:location", content: config.location });
      this.meta.updateTag({
        property: "job:location",
        content: config.location,
      });
    }

    if (config.salary) {
      this.meta.updateTag({ name: "job:salary", content: config.salary });
      this.meta.updateTag({ property: "job:salary", content: config.salary });
    }

    if (config.workType) {
      this.meta.updateTag({ name: "job:work_type", content: config.workType });
      this.meta.updateTag({
        property: "job:work_type",
        content: config.workType,
      });
    }

    if (config.employmentType) {
      this.meta.updateTag({
        name: "job:employment_type",
        content: config.employmentType,
      });
      this.meta.updateTag({
        property: "job:employment_type",
        content: config.employmentType,
      });
    }

    // Add article-specific tags for better LinkedIn recognition
    this.meta.updateTag({
      property: "article:section",
      content: "Jobs",
    });
    this.meta.updateTag({
      property: "article:tag",
      content: `${config.company}, ${config.title}, Design Jobs Careers`,
    });
  }

  setLinkedInSpecificTags() {
    // Public method for setting LinkedIn-specific meta tags
    this.meta.updateTag({
      name: "linkedin:card",
      content: "summary_large_image",
    });
    this.meta.updateTag({
      property: "article:section",
      content: "Jobs",
    });
  }

  setImageMetaTag(imageUrl: string) {
    // Public method for setting the specific image meta tag format
    this.meta.updateTag({
      name: "image",
      property: "og:image",
      content: imageUrl,
    });
  }

  setRobotsMeta(index: boolean = true, follow: boolean = true) {
    const content = `${index ? "index" : "noindex"}, ${
      follow ? "follow" : "nofollow"
    }`;
    this.meta.updateTag({ name: "robots", content });
  }

  setGeographicMetaTags(config: {
    region?: string;
    placename?: string;
    position?: string;
  }) {
    if (config.region) {
      this.meta.updateTag({ name: "geo.region", content: config.region });
    }
    if (config.placename) {
      this.meta.updateTag({ name: "geo.placename", content: config.placename });
    }
    if (config.position) {
      this.meta.updateTag({ name: "geo.position", content: config.position });
    }
  }

  setEnhancedGeographicMetaTags(config: {
    city?: string;
    country?: string;
    region?: string;
    coordinates?: { lat: number; lng: number };
    placename?: string;
  }) {
    // Clear existing geographic tags first
    this.clearGeographicMetaTags();

    if (config.placename) {
      this.meta.updateTag({ name: "geo.placename", content: config.placename });
    }

    // Set region based on country code mapping
    if (config.country && config.city) {
      const regionCode = this.getRegionCode(config.country, config.city);
      if (regionCode) {
        this.meta.updateTag({ name: "geo.region", content: regionCode });
      }
    }

    // Set coordinates if available
    if (config.coordinates) {
      const position = `${config.coordinates.lat};${config.coordinates.lng}`;
      this.meta.updateTag({ name: "geo.position", content: position });
    }

    // Add ICBM meta tag for legacy support
    if (config.coordinates) {
      const icbm = `${config.coordinates.lat}, ${config.coordinates.lng}`;
      this.meta.updateTag({ name: "ICBM", content: icbm });
    }

    // Add location-specific Open Graph locale if available
    if (config.country) {
      const locale = this.getLocaleFromCountry(config.country);
      if (locale) {
        this.meta.updateTag({ property: "og:locale", content: locale });
      }
    }
  }

  private getRegionCode(country: string, city: string): string | null {
    // Country code mappings for common locations
    const regionMappings: { [key: string]: { [key: string]: string } } = {
      Portugal: {
        Lisbon: "PT-11",
        Porto: "PT-13",
        Coimbra: "PT-06",
        Braga: "PT-03",
        Aveiro: "PT-01",
      },
      "United Kingdom": {
        London: "GB-ENG",
        Manchester: "GB-ENG",
        Birmingham: "GB-ENG",
        Edinburgh: "GB-SCT",
        Glasgow: "GB-SCT",
        Cardiff: "GB-WLS",
      },
      Spain: {
        Madrid: "ES-MD",
        Barcelona: "ES-CT",
        Valencia: "ES-VC",
        Seville: "ES-AN",
        Bilbao: "ES-PV",
      },
      Germany: {
        Berlin: "DE-BE",
        Munich: "DE-BY",
        Hamburg: "DE-HH",
        Frankfurt: "DE-HE",
        Cologne: "DE-NW",
      },
      Netherlands: {
        Amsterdam: "NL-NH",
        Rotterdam: "NL-ZH",
        "The Hague": "NL-ZH",
        Utrecht: "NL-UT",
      },
      France: {
        Paris: "FR-75",
        Lyon: "FR-69",
        Marseille: "FR-13",
        Toulouse: "FR-31",
        Nice: "FR-06",
      },
      "United States": {
        "New York": "US-NY",
        "Los Angeles": "US-CA",
        Chicago: "US-IL",
        "San Francisco": "US-CA",
        Seattle: "US-WA",
        Boston: "US-MA",
        Austin: "US-TX",
      },
    };

    return regionMappings[country]?.[city] || null;
  }

  private getLocaleFromCountry(country: string): string | null {
    const localeMap: { [key: string]: string } = {
      Portugal: "pt_PT",
      Spain: "es_ES",
      France: "fr_FR",
      Germany: "de_DE",
      Netherlands: "nl_NL",
      "United Kingdom": "en_GB",
      "United States": "en_US",
      Italy: "it_IT",
      Belgium: "nl_BE",
      Switzerland: "de_CH",
      Austria: "de_AT",
    };

    return localeMap[country] || null;
  }

  clearGeographicMetaTags() {
    this.meta.removeTag('name="geo.region"');
    this.meta.removeTag('name="geo.placename"');
    this.meta.removeTag('name="geo.position"');
    this.meta.removeTag('name="ICBM"');
    this.meta.removeTag('property="og:locale"');
  }

  setPrevUrl(url: string): void {
    const head = this.document.head;
    if (!head) return;

    let linkEl = head.querySelector<HTMLLinkElement>('link[rel="prev"]');

    if (!linkEl) {
      linkEl = this.document.createElement("link");
      linkEl.setAttribute("rel", "prev");
      head.appendChild(linkEl);
    }

    linkEl.setAttribute("href", url);
  }

  setNextUrl(url: string): void {
    const head = this.document.head;
    if (!head) return;

    let linkEl = head.querySelector<HTMLLinkElement>('link[rel="next"]');

    if (!linkEl) {
      linkEl = this.document.createElement("link");
      linkEl.setAttribute("rel", "next");
      head.appendChild(linkEl);
    }

    linkEl.setAttribute("href", url);
  }

  removePrevUrl(): void {
    const head = this.document.head;
    if (!head) return;

    const linkEl = head.querySelector<HTMLLinkElement>('link[rel="prev"]');
    if (linkEl) {
      linkEl.remove();
    }
  }

  removeNextUrl(): void {
    const head = this.document.head;
    if (!head) return;

    const linkEl = head.querySelector<HTMLLinkElement>('link[rel="next"]');
    if (linkEl) {
      linkEl.remove();
    }
  }

  private getCanonicalUrl(): string {
    const head = this.document.head;
    if (!head) return "";

    const linkEl = head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    return linkEl?.getAttribute("href") || "";
  }

  /**
   * Set structured data (JSON-LD schema) for SEO.
   * Works in both browser and SSR contexts by directly manipulating the DOM.
   *
   * @param data - The structured data object (e.g., JobPosting schema)
   * @param id - Unique ID for the script tag (e.g., 'jobPostingSchema')
   */
  setStructuredData(data: any, id: string): void {
    const head = this.document.head;
    if (!head) return;

    // Remove existing script with the same ID if present
    const existingScript = head.querySelector(`script[id="${id}"]`);
    if (existingScript) {
      existingScript.remove();
    }

    // Create new script element
    const script = this.document.createElement("script");
    script.setAttribute("id", id);
    script.setAttribute("type", "application/ld+json");
    script.textContent = JSON.stringify(data);
    head.appendChild(script);
  }

  /**
   * Remove structured data (JSON-LD schema) by ID
   * @param id - Unique ID of the script tag to remove
   */
  removeStructuredData(id: string): void {
    const head = this.document.head;
    if (!head) return;

    const existingScript = head.querySelector(`script[id="${id}"]`);
    if (existingScript) {
      existingScript.remove();
    }
  }
}
